// frontend/src/components/LinkedInFormatter.js
//
// The formatter behind /tools/linkedin-text-formatter.
//
// Runs entirely in the browser — no endpoint, no model call, no rate limit. The
// mapping lives in utils/unicodeText.js so it can be unit-tested without React.
//
// THREE THINGS THE COMPETING TOOLS DO NOT DO, which are the reason to build this
// rather than clone typegrow:
//   1. The "…see more" fold preview. LinkedIn truncates a feed post around 210
//      characters and that is where posts live or die. We already compute this
//      for the caption generator.
//   2. The accessibility meter. Screen readers announce these characters by their
//      Unicode names or skip them (verified against Scope for Business and RNIB,
//      2026-08-18). Every tool on this SERP is silent about it. Ours measures it.
//   3. Warning that LinkedIn SEARCH cannot read styled text either — a headline
//      in pseudo-bold is invisible to anyone searching those words.
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Form, Row, Col, ProgressBar, Badge } from 'react-bootstrap'
import { Clipboard, CheckLg, EraserFill, ExclamationTriangleFill } from 'react-bootstrap-icons'

import {
  STYLE_LIST, toStyle, strip, accessibilityReport, countChars,
  foldPreview, SEE_MORE_AT, POST_LIMIT,
} from '../utils/unicodeText'

const SAMPLE = `We shipped something I am proud of this week.

Three things I learned:
- Ship the smallest version that answers the question
- Measure before you argue
- The boring fix usually wins

What would you add?`

/** Nudge a UTF-16 index off the middle of a surrogate pair. */
function snap (str, i) {
  if (i > 0 && i < str.length) {
    const c = str.charCodeAt(i)
    if (c >= 0xDC00 && c <= 0xDFFF) return i - 1   // low surrogate: step back
  }
  return i
}

export default function LinkedInFormatter () {
  const [text, setText] = useState(SAMPLE)
  const [copied, setCopied] = useState(false)
  const ref = useRef(null)

  const apply = useCallback(style => {
    const el = ref.current
    let start = 0
    let end = text.length
    if (el && el.selectionStart !== el.selectionEnd) {
      start = snap(text, el.selectionStart)
      end = snap(text, el.selectionEnd)
    }
    const next = text.slice(0, start) + toStyle(text.slice(start, end), style) + text.slice(end)
    setText(next)
    // Keep the caret roughly where it was rather than jumping to the end.
    requestAnimationFrame(() => {
      if (!el) return
      el.focus()
      const grew = next.length - text.length
      try { el.setSelectionRange(start, end + grew) } catch { /* non-fatal */ }
    })
  }, [text])

  const clear = () => setText(strip(text))

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked — the textarea is selectable anyway */ }
  }

  const a11y = useMemo(() => accessibilityReport(text), [text])
  const fold = useMemo(() => foldPreview(text), [text])
  const len = countChars(text)

  const a11yVariant = { none: 'success', ok: 'success', warn: 'warning', bad: 'danger' }[a11y.level]
  const a11yMessage = {
    none: 'All plain text. Fully readable by screen readers and by LinkedIn search.',
    ok: 'Short emphasis only. This is the trade-off Scope and RNIB describe as reasonable.',
    warn: 'A lot of this post is look-alike characters. Screen readers will struggle with it.',
    bad: 'Most of this post is unreadable to a screen reader and invisible to LinkedIn search.',
  }[a11y.level]

  return (
    <div className='p-4 rounded-3' style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
      {/* ── toolbar ── */}
      <div className='d-flex flex-wrap gap-2 mb-3'>
        {STYLE_LIST.map(([key, label]) => (
          <Button key={key} size='sm' variant='outline-secondary' onClick={() => apply(key)}>
            <span style={{ fontFamily: 'inherit' }}>{toStyle(label, key)}</span>
          </Button>
        ))}
        <Button size='sm' variant='outline-danger' onClick={clear} title='Back to plain text'>
          <EraserFill className='me-1' />Clear formatting
        </Button>
      </div>
      <p className='text-muted small mb-3'>
        Select part of your post to style just that, or click a button with nothing selected to
        style the whole thing.
      </p>

      {/* ── editor ── */}
      <Form.Control
        as='textarea'
        ref={ref}
        rows={9}
        value={text}
        onChange={e => setText(e.target.value)}
        style={{ fontSize: '1rem', lineHeight: 1.6 }}
        aria-label='Your LinkedIn post'
      />

      <div className='d-flex flex-wrap gap-3 align-items-center mt-2'>
        <span className='small text-muted' style={{ fontVariantNumeric: 'tabular-nums' }}>
          {len.toLocaleString()} / {POST_LIMIT.toLocaleString()} characters
          {len > POST_LIMIT && <span className='text-danger fw-semibold ms-1'>over the limit</span>}
        </span>
        <Button size='sm' variant='primary' onClick={copy}>
          {copied ? <><CheckLg className='me-1' />Copied</> : <><Clipboard className='me-1' />Copy post</>}
        </Button>
      </div>

      <hr className='my-4' />

      <Row className='g-4'>
        {/* ── the fold preview ── */}
        <Col lg={6}>
          <h3 className='h6 fw-bold mb-2'>What people see before “…see more”</h3>
          <div className='p-3 rounded-3' style={{ border: '1px solid #e5e7eb', background: '#f8f9fa' }}>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {fold.visible}
              {fold.folded && <span className='text-muted'>… </span>}
              {fold.folded && <span className='text-primary fw-semibold'>see more</span>}
            </div>
            {fold.folded && (
              <div className='mt-2 pt-2 border-top' style={{ whiteSpace: 'pre-wrap', opacity: 0.4, lineHeight: 1.6 }}>
                {fold.hidden}
              </div>
            )}
          </div>
          <p className='text-muted small mt-2 mb-0'>
            LinkedIn folds a feed post at roughly {SEE_MORE_AT} characters on desktop, and sooner
            on mobile. The exact figure is not published, so treat this as a guide — the point is
            that the faded text below the line only gets read if the text above it earns the click.
          </p>
        </Col>

        {/* ── the accessibility meter — the differentiator ── */}
        <Col lg={6}>
          <h3 className='h6 fw-bold mb-2'>Accessibility check</h3>
          <div className='p-3 rounded-3' style={{ border: '1px solid #e5e7eb' }}>
            <div className='d-flex align-items-center gap-2 mb-2'>
              <Badge bg={a11yVariant}>{a11y.pct}% look-alike characters</Badge>
              {a11y.level === 'bad' && <ExclamationTriangleFill className='text-danger' />}
            </div>
            <ProgressBar now={a11y.pct} variant={a11yVariant} style={{ height: 8 }} className='mb-2' />
            <p className='small mb-2'>{a11yMessage}</p>
            <p className='text-muted small mb-0'>
              {a11y.styled} of {a11y.total} visible characters are Unicode look-alikes rather than
              real letters. Screen readers announce these by their Unicode names or skip them
              entirely, and LinkedIn search cannot match them.
            </p>
          </div>
        </Col>
      </Row>
    </div>
  )
}
